<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { HomeElevationType } from '@/types/propertyTaxUpload/propertyTaxUpload'
import { usePropertyTaxUploadStore } from '@/stores/propertyTaxUploadStore'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

const uploadStore = usePropertyTaxUploadStore()
const { flightProjectData, validateErrors } = storeToRefs(uploadStore)

const inputFlightAltitude = computed<string>({
  get() {
    return flightProjectData.value.flightAltitude?.toString() ?? ''
  },
  set(val: string) {
    const sanitized = sanitizeValue(val)
    if (sanitized == null || sanitized == '') {
      flightProjectData.value.flightAltitude = undefined
    } else {
      flightProjectData.value.flightAltitude = parseInt(sanitized)
    }
  }
})

const inputAltitude = computed<string>({
  get() {
    return flightProjectData.value.homeElevationValue?.toString() ?? ''
  },
  set(val: string) {
    const sanitized = sanitizeValue(val)
    if (sanitized == null || sanitized == '') {
      flightProjectData.value.homeElevationValue = undefined
    } else {
      flightProjectData.value.homeElevationValue = parseInt(sanitized)
    }
  }
})

const sanitizeValue = (value: string): string => {
  let val = value
  val = val.replace(/[^0-9]/g, '')
  val = val.replace(/^0+(?=\d)/, '')
  return val
}

function onFlightAltitudeInput(e: Event) {
  const target = e.target as HTMLInputElement
  const sanitized = sanitizeValue(target.value)
  target.value = sanitized
  inputFlightAltitude.value = sanitized
}

function onFlightAltitudeBlur() {
  if (inputFlightAltitude.value === '' || inputFlightAltitude.value === undefined) {
    flightProjectData.value.flightAltitude = undefined
  } else {
    const val = parseInt(inputFlightAltitude.value, 10)
    flightProjectData.value.flightAltitude = val
    inputFlightAltitude.value = val.toString()
  }
}

function onAltitudeInput(e: Event) {
  const target = e.target as HTMLInputElement
  const sanitized = sanitizeValue(target.value)
  target.value = sanitized
  inputAltitude.value = sanitized
}

function onAltitudeBlur() {
  if (inputAltitude.value === '' || inputAltitude.value === undefined) {
    flightProjectData.value.homeElevationValue = undefined
  } else {
    const val = parseInt(inputAltitude.value, 10)
    flightProjectData.value.homeElevationValue = val
    inputAltitude.value = val.toString()
  }
}
</script>

<template>
  <div class="top-section">
    <div class="project-select">
      <fieldset>
        <div class="row g-2 align-items-center">
          <label for="project-select" class="col-auto col-form-label mb-0">プロジェクト</label>
          <div class="col-auto">
            <select id="project-select" v-model="flightProjectData.projectId" class="form-select">
              <option v-for="proj in flightProjectData.projectList" :key="proj.id" :value="proj.id">
                {{ proj.name }}
              </option>
            </select>
          </div>
        </div>
      </fieldset>
    </div>
    <div class="flight-row">
      <div class="flight-input">
        <!-- ラジオ群 -->
        <fieldset class="radio-group">
          <div class="radio-item">
            <input
              type="radio"
              id="none"
              :value="HomeElevationType.NOT_APPLICABLE"
              v-model="flightProjectData.homeElevationType"
            />
            <label for="none">フライト開始地点の情報を入力しない</label>
          </div>
          <div class="radio-item">
            <input
              type="radio"
              id="latlng"
              :value="HomeElevationType.LATLNG"
              v-model="flightProjectData.homeElevationType"
            />
            <label for="latlng">フライト開始地点の緯度経度を入力する</label>
          </div>
          <div class="radio-item">
            <input
              type="radio"
              id="altitude"
              :value="HomeElevationType.ALTITUDE"
              v-model="flightProjectData.homeElevationType"
            />
            <label for="altitude">フライト開始地点の高度を入力する</label>
          </div>
          <div class="radio-item">
            <input
              type="radio"
              id="filename"
              :value="HomeElevationType.FILE_NAME"
              v-model="flightProjectData.homeElevationType"
            />
            <label for="filename">フライト開始地点の撮影ファイル名を入力する</label>
          </div>
        </fieldset>

        <!-- 対応フォーム群 -->
        <fieldset class="form-group">
          <!-- フライト高度 -->
          <div class="form-row">
            <label for="flight-altitude" class="form-label mb-0">フライト高度 (m)</label>
            <input
              type="text"
              id="flight-altitude"
              :value="inputFlightAltitude"
              class="form-control input-limited-width"
              placeholder="XX"
              @input="onFlightAltitudeInput"
              @blur="onFlightAltitudeBlur"
            />
          </div>
          <!-- 緯度経度 -->
          <fieldset
            class="form-row latlng"
            :disabled="flightProjectData.homeElevationType !== HomeElevationType.LATLNG"
          >
            <div class="lat-label-group align-items-center d-flex gap-1">
              <label for="latitude" class="form-label latitude mb-0"
                >緯度<FontAwesomeIcon
                  v-if="flightProjectData.homeElevationType === HomeElevationType.LATLNG"
                  :icon="['fas', 'asterisk']"
                  class="required-icon"
              /></label>
              <input
                type="number"
                id="latitude"
                v-model="flightProjectData.homeElevationLatlng.latitude"
                class="form-control input-limited-width-longitude"
                placeholder="XX.XXXXX"
              />
            </div>

            <div class="lng-label-group align-items-center d-flex gap-1">
              <label for="longitude" class="form-label longitude mb-0"
                >経度<FontAwesomeIcon
                  v-if="flightProjectData.homeElevationType === HomeElevationType.LATLNG"
                  :icon="['fas', 'asterisk']"
                  class="required-icon"
              /></label>
              <input
                type="number"
                id="longitude"
                v-model="flightProjectData.homeElevationLatlng.longitude"
                class="form-control input-limited-width-longitude"
                placeholder="XX.XXXXX"
              />
            </div>
            <div v-if="validateErrors.homeElevationLatlng" class="text-danger">
              {{ validateErrors.homeElevationLatlng }}
            </div>
          </fieldset>

          <!-- フライト開始地点の高度 -->
          <fieldset
            class="form-row"
            :disabled="flightProjectData.homeElevationType !== HomeElevationType.ALTITUDE"
          >
            <label for="altitude" class="form-label mb-0"
              >フライト開始地点の高度 (m)<FontAwesomeIcon
                v-if="flightProjectData.homeElevationType === HomeElevationType.ALTITUDE"
                :icon="['fas', 'asterisk']"
                class="required-icon"
            /></label>
            <input
              type="text"
              id="altitude"
              :value="inputAltitude"
              class="form-control input-limited-width"
              placeholder="XX"
              @input="onAltitudeInput"
              @blur="onAltitudeBlur"
            />
            <div v-if="validateErrors.homeElevationValue" class="text-danger">
              {{ validateErrors.homeElevationValue }}
            </div>
          </fieldset>

          <!-- 撮影ファイル名 -->
          <fieldset
            class="form-row filename"
            :disabled="flightProjectData.homeElevationType !== HomeElevationType.FILE_NAME"
          >
            <label for="filename" class="form-label mb-0">
              撮影ファイル名<FontAwesomeIcon
                v-if="flightProjectData.homeElevationType === HomeElevationType.FILE_NAME"
                :icon="['fas', 'asterisk']"
                class="required-icon"
            /></label>
            <input
              type="text"
              id="filename"
              v-model="flightProjectData.homeElevationImgFileName"
              class="form-control input-limited-width"
              placeholder="ファイル名を入力してください"
            />
            <div v-if="validateErrors.homeElevationImgFileName" class="text-danger">
              {{ validateErrors.homeElevationImgFileName }}
            </div>
          </fieldset>
        </fieldset>
      </div>
      <div>
        <FontAwesomeIcon
          :icon="['fas', 'asterisk']"
          class="required-icon"
        />のある項目は必須で入力してください
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.top-section {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;

  display: flex;
  gap: 40px;
  flex-wrap: wrap;
}

.project-select {
  flex: 1 1 250px;
}

.required-icon {
  color: #dc3545;
}

.flight-row {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 3 1 700px;
}

.flight-input {
  display: flex;
  gap: 24px;
  align-items: center;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 200px;
}

.radio-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
}

.radio-item input[type='radio'] {
  vertical-align: middle;
  margin-top: 0; /* 余計なマージントップがあれば消す */
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 300px;
  margin: 0;
}

.form-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-row.latlng {
  gap: 14px;
}

.form-row.filename {
  padding-bottom: 4px;
}

.form-label {
  white-space: nowrap;
  text-align: right;
  display: inline-flex;
  align-items: center;
}
.form-label.latitude,
.form-label.longitude {
  min-width: 30px;
}

.lat-label-group,
.lng-label-group {
  display: flex;
  align-items: center;
  gap: 19px;
}

.input-limited-width {
  max-width: 200px;
  width: 100%;
}

.input-limited-width-latitude,
.input-limited-width-longitude {
  max-width: 100px;
  width: 100%;
}

.lng-label-group label {
  min-width: 50px;
  text-align: right;
}
</style>
